import { collectFolderSubtreeIds, applyTreeDropWithPosition } from '../../utils/collectionFolders';
import { parseRowId as parseDnDId } from '../../utils/treeSelection';

// ─── DnD ID helpers ──────────────────────────────────────────────────────────
// The `collection-<id>` / `folder-<id>` / `request-<id>` scheme is shared with the
// multi-selection layer, so its parser lives in `utils/treeSelection.js` and is
// imported above as `parseDnDId` — one scheme, one parser.

export function findCollectionIndexForDrag(collections, activeP) {
  if (!activeP) return -1;
  for (let ci = 0; ci < collections.length; ci++) {
    const c = collections[ci];
    if (activeP.kind === 'collection' && String(c.id) === String(activeP.key)) return ci;
    if (activeP.kind === 'folder') {
      if ((c.settings?.folders || []).some((f) => String(f.id) === String(activeP.key))) return ci;
    }
    if (activeP.kind === 'request') {
      if ((c.requests || []).some((r) => String(r.id) === String(activeP.key))) return ci;
    }
  }
  return -1;
}

export function arrayMove(arr, from, to) {
  const result = [...arr];
  result.splice(to, 0, result.splice(from, 1)[0]);
  return result;
}

/** Reassign only collection sort_order to array index; leave folder/request order intact. */
export function withCollectionOrder(cols) {
  return cols.map((c, ci) => ({ ...c, sort_order: ci }));
}

/**
 * Validate a prospective drop and return the indicator descriptor `{ overId, position }`,
 * or null when the drop is not legal (so no indicator is drawn).
 */
export function computeDropIndicator(collections, activeId, overId, position) {
  const activeP = parseDnDId(activeId);
  const overP   = parseDnDId(overId);
  if (!activeP || !overP || activeId === overId || !position) return null;

  if (activeP.kind === 'collection') {
    if (overP.kind !== 'collection') return null;
    return { overId, position };
  }

  const sourceCi = findCollectionIndexForDrag(collections, activeP);
  const targetCi =
    overP.kind === 'collection'
      ? collections.findIndex((c) => String(c.id) === String(overP.key))
      : findCollectionIndexForDrag(collections, overP);
  if (sourceCi === -1 || targetCi === -1) return null;
  if (collections[targetCi].can_edit === false) return null;

  // A folder may never land within its own subtree (same collection only).
  if (activeP.kind === 'folder' && sourceCi === targetCi && overP.kind === 'folder') {
    const folders = collections[sourceCi].settings?.folders || [];
    if (collectFolderSubtreeIds(folders, activeP.key).has(String(overP.key))) return null;
  }
  return { overId, position };
}

/**
 * Relocate the active item (a request, or a folder subtree) into the target collection,
 * then position it relative to the over-target with applyTreeDropWithPosition.
 */
export function moveAcrossCollections(collections, sourceCi, targetCi, activeP, overP, position) {
  const source = collections[sourceCi];
  const target = collections[targetCi];

  if (activeP.kind === 'request') {
    const moving = (source.requests || []).find((r) => String(r.id) === String(activeP.key));
    if (!moving) return null;
    const newSource = {
      ...source,
      requests: (source.requests || []).filter((r) => String(r.id) !== String(activeP.key)),
    };
    const movedTarget = { ...target, requests: [...(target.requests || []), { ...moving }] };
    const positioned =
      applyTreeDropWithPosition(movedTarget, 'request', activeP.key, overP.kind, overP.key, position) ||
      movedTarget;
    return collections.map((c, i) =>
      i === sourceCi ? newSource : i === targetCi ? positioned : c
    );
  }

  // Folder subtree move.
  const sourceFolders = source.settings?.folders || [];
  const subtreeIds = collectFolderSubtreeIds(sourceFolders, activeP.key);
  const movingFolders = sourceFolders.filter((f) => subtreeIds.has(String(f.id)));
  const movingRequests = (source.requests || []).filter(
    (r) => r.folder_id && subtreeIds.has(String(r.folder_id))
  );

  const newSource = {
    ...source,
    settings: {
      ...(source.settings || {}),
      folders: sourceFolders.filter((f) => !subtreeIds.has(String(f.id))),
    },
    requests: (source.requests || []).filter(
      (r) => !r.folder_id || !subtreeIds.has(String(r.folder_id))
    ),
  };

  const movedTarget = {
    ...target,
    settings: {
      ...(target.settings || {}),
      folders: [...(target.settings?.folders || []), ...movingFolders.map((f) => ({ ...f }))],
    },
    requests: [...(target.requests || []), ...movingRequests.map((r) => ({ ...r }))],
  };

  const positioned =
    applyTreeDropWithPosition(movedTarget, 'folder', activeP.key, overP.kind, overP.key, position) ||
    movedTarget;

  return collections.map((c, i) =>
    i === sourceCi ? newSource : i === targetCi ? positioned : c
  );
}

/** Position-aware drag-end reducer — returns next collections or null on no-op/invalid. */
export function applySidebarDrop(collections, activeId, overId, position) {
  const activeP = parseDnDId(activeId);
  const overP   = parseDnDId(overId);
  if (!activeP || !overP || activeId === overId) return null;

  if (activeP.kind === 'collection') {
    if (overP.kind !== 'collection') return null;
    const oldIndex = collections.findIndex((c) => String(c.id) === String(activeP.key));
    let   newIndex = collections.findIndex((c) => String(c.id) === String(overP.key));
    if (oldIndex === -1 || newIndex === -1) return null;
    // 'after' drops land below the target collection.
    if (position === 'after' && newIndex < oldIndex) newIndex += 1;
    else if (position === 'before' && newIndex > oldIndex) newIndex -= 1;
    if (oldIndex === newIndex) return null;
    return withCollectionOrder(arrayMove(collections, oldIndex, newIndex));
  }

  const sourceCi = findCollectionIndexForDrag(collections, activeP);
  if (sourceCi === -1) return null;

  const targetCi =
    overP.kind === 'collection'
      ? collections.findIndex((c) => String(c.id) === String(overP.key))
      : findCollectionIndexForDrag(collections, overP);
  if (targetCi === -1) return null;
  if (collections[targetCi].can_edit === false) return null;

  if (sourceCi === targetCi) {
    const col = collections[sourceCi];
    const next = applyTreeDropWithPosition(col, activeP.kind, activeP.key, overP.kind, overP.key, position);
    if (!next || next === col) return null;
    return collections.map((c, i) => (i === sourceCi ? next : c));
  }

  return moveAcrossCollections(collections, sourceCi, targetCi, activeP, overP, position);
}
